import { BackendApiError } from './minecardsBackendClient'

const transferCodePattern = /^[A-Za-z0-9_-]{43}$/

const requireProfileId = (value) => {
    if (value === null || value === undefined || value === '') {
        throw new BackendApiError('Backend returned an invalid profile ID', {
            code: 'invalid_response',
        })
    }
    const profileId = Number(value)
    if (!Number.isSafeInteger(profileId) || profileId < 0) {
        throw new BackendApiError('Backend returned an invalid profile ID', {
            code: 'invalid_response',
        })
    }
    return profileId
}

const profileFromPayload = (payload) => ({
    ...payload?.profile,
    id: requireProfileId(payload?.profile?.id),
})

const authenticatedHeaders = async (user) => {
    if (!user || typeof user.getIdToken !== 'function') {
        throw new BackendApiError('Firebase authentication is required', {
            code: 'unauthenticated',
        })
    }
    return {
        Accept: 'application/json',
        Authorization: `Bearer ${await user.getIdToken()}`,
    }
}

const request = async (path, options, fetchImpl, baseUrl) => {
    let response
    try {
        response = await fetchImpl(
            `${String(baseUrl).replace(/\/+$/, '')}${path}`,
            { credentials: 'include', ...options }
        )
    } catch (error) {
        throw new BackendApiError('Backend request failed', {
            code: 'network_error',
            cause: error,
        })
    }

    const rawBody = await response.text()
    let payload = null
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody)
        } catch (error) {
            throw new BackendApiError('Backend returned invalid JSON', {
                status: response.status,
                code: 'invalid_response',
                cause: error,
            })
        }
    }
    if (!response.ok) {
        const apiError = payload?.error || {}
        throw new BackendApiError(
            apiError.message || `Backend request failed (${response.status})`,
            {
                status: response.status,
                code: apiError.code || 'http_error',
                requestId: apiError.requestId,
            }
        )
    }
    return payload
}

export const createDomainSessionTransfer = async (
    user,
    {
        baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || '',
        fetchImpl = window.fetch.bind(window),
    } = {}
) => {
    const payload = await request(
        '/api/v1/domain-session-transfers',
        {
            method: 'POST',
            headers: await authenticatedHeaders(user),
        },
        fetchImpl,
        baseUrl
    )
    if (!transferCodePattern.test(payload?.code || '')) {
        throw new BackendApiError('Backend returned an invalid transfer code', {
            code: 'invalid_response',
        })
    }
    return { ...payload, profileId: requireProfileId(payload?.profileId) }
}

export const prepareDomainMigrationProfile = async (
    user,
    {
        baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || '',
        fetchImpl = window.fetch.bind(window),
    } = {}
) => {
    const payload = await request(
        '/api/v1/me',
        {
            method: 'GET',
            headers: await authenticatedHeaders(user),
        },
        fetchImpl,
        baseUrl
    )
    return profileFromPayload(payload)
}

export const readTransferredDomainMigrationProfile = async ({
    baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || '',
    fetchImpl = window.fetch.bind(window),
} = {}) => {
    const payload = await request(
        '/api/v1/me',
        {
            method: 'GET',
            headers: { Accept: 'application/json' },
        },
        fetchImpl,
        baseUrl
    )
    return profileFromPayload(payload)
}

export const redeemDomainSessionTransfer = async (
    code,
    {
        baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || '',
        fetchImpl = window.fetch.bind(window),
    } = {}
) => {
    if (!transferCodePattern.test(code || '')) {
        throw new BackendApiError('Transfer code is invalid', {
            code: 'invalid_transfer_code',
        })
    }
    const payload = await request(
        '/api/v1/domain-session-transfers/redeem',
        {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
        },
        fetchImpl,
        baseUrl
    )
    return { ...payload, profileId: requireProfileId(payload?.profileId) }
}
